import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import Nav from './Nav';
import Footer from './Footer';
import SEOHead from './SEOHead';
import { CONTACT_API_URL } from '../config/api';

const ContactUs = () => {
    const { language } = useLanguage();
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        subject: '',
        message: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitStatus(null);

        try {
            // Send to backend API using environment-specific URL
            const response = await fetch(CONTACT_API_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData)
            });

            const result = await response.json();

            if (result.success) {
                setSubmitStatus('success');
                setFormData({ name: '', email: '', subject: '', message: '' });
                console.log('Email sent successfully:', result.messageId);
            } else {
                console.error('API Error:', result.error);
                setSubmitStatus('error');
            }
        } catch (error) {
            console.error('Error submitting form:', error);
            setSubmitStatus('error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const contactContent = {
        en: {
            title: "Contact",
            subtitle: "Get in touch with our team",
            description: "Questions about Audiosum? We're here to help! Our team provides support for technical issues, feature questions, partnerships, and feedback. Get personalized assistance for your audio transcription and AI summarization needs.",
            form: {
                name: "Full Name",
                email: "Email Address",
                subject: "Subject",
                message: "Message",
                submit: "Send Message",
                sending: "Sending..."
            },
            success: "Thank you! Your message has been sent successfully. We'll get back to you soon.",
            error: "Sorry, there was an error sending your message. Please try again."
        },
        ar: {
            title: "اتصل",
            subtitle: "تواصل مع فريقنا",
            description: "أسئلة حول Audiosum؟ نحن هنا للمساعدة! يقدم فريقنا الدعم للمسائل التقنية وأسئلة الميزات والشراكات والملاحظات. احصل على مساعدة شخصية لاحتياجات تحويل الصوت والتلخيص بالذكاء الاصطناعي.",
            form: {
                name: "الاسم الكامل",
                email: "البريد الإلكتروني",
                subject: "الموضوع",
                message: "الرسالة",
                submit: "إرسال الرسالة",
                sending: "جاري الإرسال..."
            },
            success: "شكراً لك! لقد تم إرسال رسالتك بنجاح. سنعود إليك قريباً.",
            error: "عذراً، حدث خطأ في إرسال رسالتك. يرجى المحاولة مرة أخرى."
        }
    };

    const currentContent = contactContent[language] || contactContent.en;

    // SEO content for contact page
    const seoContent = {
        en: {
            title: "Contact Us | Audiosum AI - Get Support & Connect with Our Team",
            description: "Contact Audiosum AI for technical support, business inquiries, partnerships, and feedback. Get personalized assistance for audio transcription and AI summarization.",
            keywords: "contact audiosum, support, technical help, business inquiries, partnerships, audio transcription support"
        },
        ar: {
            title: "اتصل بنا | أوديوسوم AI - احصل على الدعم وتواصل مع فريقنا",
            description: "تواصل مع أوديوسوم AI للحصول على الدعم التقني والاستفسارات التجارية والشراكات والملاحظات. احصل على مساعدة شخصية لتحويل الصوت والتلخيص بالذكاء الاصطناعي.",
            keywords: "تواصل أوديوسوم، دعم، مساعدة تقنية، استفسارات الأعمال، شراكات، دعم تحويل الصوت"
        }
    };

    const currentSEO = seoContent[language] || seoContent.en;

    return (
        <div className="contact-page">
            <SEOHead 
                title={currentSEO.title}
                description={currentSEO.description}
                keywords={currentSEO.keywords}
            />
            <Nav />
            
            <div className="contact-hero" style={{ 
                padding: '150px 0 30px', // Reduced bottom padding
                textAlign: 'center'
            }}>
                <div className="container">
                    <h1 className="display-4 mb-4" style={{ fontFamily: '"Roboto", "Poppins", sans-serif', color: '#0F172A' }}>{currentContent.title}</h1>
                    <p className="lead mb-0" style={{ fontFamily: '"Tinos", "Times New Roman", serif', color: '#64748b' }}>{currentContent.subtitle}</p>
                </div>
            </div>

            <div className="contact-content" style={{ padding: '40px 0 80px' }}>
                <div className="container">
                    <div className="row justify-content-center">
                        <div className="col-lg-8">
                            <div className="text-center mb-5">
                                <p style={{ 
                                    fontSize: '1.1rem', 
                                    lineHeight: '1.6',
                                    color: '#475569',
                                    fontWeight: '400',
                                    marginBottom: '2rem'
                                }}>
                                    {currentContent.description}
                                </p>
                            </div>

                            <div className="contact-form-wrapper" style={{
                                background: '#FFFFFF',
                                backdropFilter: 'blur(10px)',
                                WebkitBackdropFilter: 'blur(10px)',
                                border: '1px solid #E2E8F0',
                                borderRadius: '24px',
                                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06)',
                                padding: '3rem',
                                position: 'relative',
                                overflow: 'hidden'
                            }}>
                                {/* Decorative gradient blobs */}
                                <div style={{
                                    position: 'absolute',
                                    top: '-30%',
                                    right: '-15%',
                                    width: '250px',
                                    height: '250px',
                                    background: 'radial-gradient(circle, rgba(6, 149, 255, 0.1) 0%, transparent 70%)',
                                    borderRadius: '50%',
                                    filter: 'blur(40px)',
                                    zIndex: 0
                                }}></div>
                                <div style={{
                                    position: 'absolute',
                                    bottom: '-20%',
                                    left: '-10%',
                                    width: '200px',
                                    height: '200px',
                                    background: 'radial-gradient(circle, rgba(118, 75, 162, 0.08) 0%, transparent 70%)',
                                    borderRadius: '50%',
                                    filter: 'blur(40px)',
                                    zIndex: 0
                                }}></div>

                                <form onSubmit={handleSubmit} style={{ position: 'relative', zIndex: 1 }}>
                                    <div className="row">
                                        <div className="col-md-6 mb-4">
                                            <label className="form-label" style={{ color: '#0F172A', fontWeight: '600', marginBottom: '0.75rem', fontSize: '0.95rem' }}>{currentContent.form.name}</label>
                                            <input
                                                type="text"
                                                className="form-control form-control-lg"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleChange}
                                                required
                                                style={{
                                                    borderRadius: '12px',
                                                    border: '1px solid #E2E8F0',
                                                    background: '#FFFFFF',
                                                    backdropFilter: 'blur(5px)',
                                                    color: '#0F172A',
                                                    padding: '0.75rem 1rem',
                                                    transition: 'all 0.3s ease'
                                                }}
                                                onFocus={(e) => {
                                                    e.target.style.border = '1px solid rgba(6, 149, 255, 0.5)';
                                                    e.target.style.background = 'rgba(255, 255, 255, 0.08)';
                                                    e.target.style.boxShadow = '0 0 20px rgba(6, 149, 255, 0.2)';
                                                }}
                                                onBlur={(e) => {
                                                    e.target.style.border = '1px solid rgba(255, 255, 255, 0.1)';
                                                    e.target.style.background = 'rgba(255, 255, 255, 0.05)';
                                                    e.target.style.boxShadow = 'none';
                                                }}
                                            />
                                        </div>
                                        <div className="col-md-6 mb-4">
                                            <label className="form-label" style={{ color: '#0F172A', fontWeight: '600', marginBottom: '0.75rem', fontSize: '0.95rem' }}>{currentContent.form.email}</label>
                                            <input
                                                type="email"
                                                className="form-control form-control-lg"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleChange}
                                                required
                                                style={{
                                                    borderRadius: '12px',
                                                    border: '1px solid #E2E8F0',
                                                    background: '#FFFFFF',
                                                    backdropFilter: 'blur(5px)',
                                                    color: '#0F172A',
                                                    padding: '0.75rem 1rem',
                                                    transition: 'all 0.3s ease'
                                                }}
                                                onFocus={(e) => {
                                                    e.target.style.border = '1px solid rgba(6, 149, 255, 0.5)';
                                                    e.target.style.background = 'rgba(255, 255, 255, 0.08)';
                                                    e.target.style.boxShadow = '0 0 20px rgba(6, 149, 255, 0.2)';
                                                }}
                                                onBlur={(e) => {
                                                    e.target.style.border = '1px solid rgba(255, 255, 255, 0.1)';
                                                    e.target.style.background = 'rgba(255, 255, 255, 0.05)';
                                                    e.target.style.boxShadow = 'none';
                                                }}
                                            />
                                        </div>
                                    </div>
                                    
                                    <div className="mb-4">
                                        <label className="form-label" style={{ color: '#0F172A', fontWeight: '600', marginBottom: '0.75rem', fontSize: '0.95rem' }}>{currentContent.form.subject}</label>
                                        <input
                                            type="text"
                                            className="form-control form-control-lg"
                                            name="subject"
                                            value={formData.subject}
                                            onChange={handleChange}
                                            required
                                            style={{
                                                borderRadius: '12px',
                                                border: '1px solid #E2E8F0',
                                                background: '#FFFFFF',
                                                backdropFilter: 'blur(5px)',
                                                color: '#0F172A',
                                                padding: '0.75rem 1rem',
                                                transition: 'all 0.3s ease'
                                            }}
                                            onFocus={(e) => {
                                                e.target.style.border = '1px solid rgba(6, 149, 255, 0.5)';
                                                e.target.style.background = 'rgba(255, 255, 255, 0.08)';
                                                e.target.style.boxShadow = '0 0 20px rgba(6, 149, 255, 0.2)';
                                            }}
                                            onBlur={(e) => {
                                                e.target.style.border = '1px solid rgba(255, 255, 255, 0.1)';
                                                e.target.style.background = 'rgba(255, 255, 255, 0.05)';
                                                e.target.style.boxShadow = 'none';
                                            }}
                                        />
                                    </div>
                                    
                                    <div className="mb-4">
                                        <label className="form-label" style={{ color: '#0F172A', fontWeight: '600', marginBottom: '0.75rem', fontSize: '0.95rem' }}>{currentContent.form.message}</label>
                                        <textarea
                                            className="form-control form-control-lg"
                                            name="message"
                                            rows="6"
                                            value={formData.message}
                                            onChange={handleChange}
                                            required
                                            style={{
                                                borderRadius: '12px',
                                                border: '1px solid #E2E8F0',
                                                background: '#FFFFFF',
                                                backdropFilter: 'blur(5px)',
                                                color: '#0F172A',
                                                padding: '0.75rem 1rem',
                                                transition: 'all 0.3s ease',
                                                resize: 'vertical'
                                            }}
                                            onFocus={(e) => {
                                                e.target.style.border = '1px solid rgba(6, 149, 255, 0.5)';
                                                e.target.style.background = 'rgba(255, 255, 255, 0.08)';
                                                e.target.style.boxShadow = '0 0 20px rgba(6, 149, 255, 0.2)';
                                            }}
                                            onBlur={(e) => {
                                                e.target.style.border = '1px solid rgba(255, 255, 255, 0.1)';
                                                e.target.style.background = 'rgba(255, 255, 255, 0.05)';
                                                e.target.style.boxShadow = 'none';
                                            }}
                                        ></textarea>
                                    </div>

                                    {submitStatus === 'success' && (
                                        <div className="alert alert-success" style={{ 
                                            borderRadius: '12px',
                                            background: 'rgba(16, 185, 129, 0.1)',
                                            border: '1px solid rgba(16, 185, 129, 0.3)',
                                            color: '#10b981',
                                            backdropFilter: 'blur(10px)',
                                            marginBottom: '1.5rem'
                                        }}>
                                            <i className="fas fa-check-circle me-2"></i>
                                            {currentContent.success}
                                        </div>
                                    )}

                                    {submitStatus === 'error' && (
                                        <div className="alert alert-danger" style={{ 
                                            borderRadius: '12px',
                                            background: 'rgba(239, 68, 68, 0.1)',
                                            border: '1px solid rgba(239, 68, 68, 0.3)',
                                            color: '#ef4444',
                                            backdropFilter: 'blur(10px)',
                                            marginBottom: '1.5rem'
                                        }}>
                                            <i className="fas fa-exclamation-circle me-2"></i>
                                            {currentContent.error}
                                        </div>
                                    )}

                                    <div className="text-center">
                                        <button
                                            type="submit"
                                            className="btn btn-primary btn-lg px-5"
                                            disabled={isSubmitting}
                                            style={{
                                                borderRadius: '25px',
                                                background: 'linear-gradient(135deg, #6C3AED, #0EA5E9)',
                                                border: 'none',
                                                padding: '14px 40px',
                                                fontSize: '1.1rem',
                                                fontWeight: '600',
                                                transition: 'all 0.3s ease',
                                                boxShadow: '0 8px 30px rgba(108, 58, 237, 0.3)',
                                                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                                                opacity: isSubmitting ? 0.7 : 1
                                            }}
                                            onMouseEnter={(e) => {
                                                if (!isSubmitting) {
                                                    e.target.style.background = '#0578D6';
                                                    e.target.style.transform = 'translateY(-2px)';
                                                    e.target.style.boxShadow = '0 12px 40px rgba(6, 149, 255, 0.5)';
                                                }
                                            }}
                                            onMouseLeave={(e) => {
                                                if (!isSubmitting) {
                                                    e.target.style.background = '#0695FF';
                                                    e.target.style.transform = 'translateY(0)';
                                                    e.target.style.boxShadow = '0 8px 30px rgba(6, 149, 255, 0.4)';
                                                }
                                            }}
                                        >
                                            {isSubmitting ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                    {currentContent.form.sending}
                                                </>
                                            ) : (
                                                currentContent.form.submit
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Additional Content Section */}
            <div className="contact-info" style={{ 
                padding: '80px 0', 
                backgroundColor: '#F8FAFC',
                fontFamily: '"Nunito Sans", "Montserrat", "Trebuchet MS", sans-serif'
            }}>
                <div className="container">
                    <div className="row justify-content-center">
                        <div className="col-12 col-lg-10">
                            <div className="row">
                                <div className="col-lg-4 mb-4">
                                    <div style={{
                                        background: 'rgba(102, 126, 234, 0.08)',
                                        backdropFilter: 'blur(10px)',
                                        WebkitBackdropFilter: 'blur(10px)',
                                        border: '1px solid rgba(102, 126, 234, 0.2)',
                                        borderRadius: '20px',
                                        padding: '2.5rem 1.5rem',
                                        boxShadow: '0 8px 32px rgba(102, 126, 234, 0.15)',
                                        textAlign: 'center',
                                        transition: 'all 0.3s ease',
                                        height: '100%'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = 'translateY(-5px)';
                                        e.currentTarget.style.boxShadow = '0 12px 40px rgba(102, 126, 234, 0.25)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.boxShadow = '0 8px 32px rgba(102, 126, 234, 0.15)';
                                    }}>
                                        <h4 style={{ 
                                            color: '#667eea', 
                                            marginBottom: '20px',
                                            fontSize: '1.5rem',
                                            fontWeight: '700',
                                            fontFamily: '"Roboto", "Poppins", sans-serif'
                                        }}>
                                            {language === 'ar' ? 'الدعم التقني' : 'Technical Support'}
                                        </h4>
                                        <p style={{ 
                                            lineHeight: '1.7',
                                            fontSize: '1rem',
                                            fontFamily: '"Inter", sans-serif',
                                            color: '#475569',
                                            marginBottom: 0
                                        }}>
                                            {language === 'ar' 
                                                ? 'نقدم دعماً تقنياً شاملاً لجميع ميزات Audiosum AI. فريقنا متاح لمساعدتك في استخدام تقنيات الذكاء الاصطناعي للنسخ والتلخيص بفعالية.'
                                                : 'We provide comprehensive technical support for all Audiosum AI features. Our team is available to help you utilize AI transcription and summarization technologies effectively.'
                                            }
                                        </p>
                                    </div>
                                </div>
                                <div className="col-lg-4 mb-4">
                                    <div style={{
                                        background: 'rgba(16, 185, 129, 0.08)',
                                        backdropFilter: 'blur(10px)',
                                        WebkitBackdropFilter: 'blur(10px)',
                                        border: '1px solid rgba(16, 185, 129, 0.2)',
                                        borderRadius: '20px',
                                        padding: '2.5rem 1.5rem',
                                        boxShadow: '0 8px 32px rgba(16, 185, 129, 0.15)',
                                        textAlign: 'center',
                                        transition: 'all 0.3s ease',
                                        height: '100%'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = 'translateY(-5px)';
                                        e.currentTarget.style.boxShadow = '0 12px 40px rgba(16, 185, 129, 0.25)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.boxShadow = '0 8px 32px rgba(16, 185, 129, 0.15)';
                                    }}>
                                        <h4 style={{ 
                                            color: '#10b981', 
                                            marginBottom: '20px',
                                            fontSize: '1.5rem',
                                            fontWeight: '700',
                                            fontFamily: '"Roboto", "Poppins", sans-serif'
                                        }}>
                                            {language === 'ar' ? 'استفسارات الأعمال' : 'Business Inquiries'}
                                        </h4>
                                        <p style={{ 
                                            lineHeight: '1.7',
                                            fontSize: '1rem',
                                            fontFamily: '"Inter", sans-serif',
                                            color: '#475569',
                                            marginBottom: 0
                                        }}>
                                            {language === 'ar' 
                                                ? 'لاستفسارات الأعمال والشراكات والحلول المؤسسية، تواصل معنا لمناقشة كيف يمكن لـ Audiosum AI تحسين سير العمل في مؤسستك.'
                                                : 'For business inquiries, partnerships, and enterprise solutions, contact us to discuss how Audiosum AI can enhance your organization\'s workflow.'
                                            }
                                        </p>
                                    </div>
                                </div>
                                <div className="col-lg-4 mb-4">
                                    <div style={{
                                        background: 'rgba(245, 158, 11, 0.08)',
                                        backdropFilter: 'blur(10px)',
                                        WebkitBackdropFilter: 'blur(10px)',
                                        border: '1px solid rgba(245, 158, 11, 0.2)',
                                        borderRadius: '20px',
                                        padding: '2.5rem 1.5rem',
                                        boxShadow: '0 8px 32px rgba(245, 158, 11, 0.15)',
                                        textAlign: 'center',
                                        transition: 'all 0.3s ease',
                                        height: '100%'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = 'translateY(-5px)';
                                        e.currentTarget.style.boxShadow = '0 12px 40px rgba(245, 158, 11, 0.25)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.boxShadow = '0 8px 32px rgba(245, 158, 11, 0.15)';
                                    }}>
                                        <h4 style={{ 
                                            color: '#f59e0b', 
                                            marginBottom: '20px',
                                            fontSize: '1.5rem',
                                            fontWeight: '700',
                                            fontFamily: '"Roboto", "Poppins", sans-serif'
                                        }}>
                                            {language === 'ar' ? 'ملاحظات المستخدمين' : 'User Feedback'}
                                        </h4>
                                        <p style={{ 
                                            lineHeight: '1.7',
                                            fontSize: '1rem',
                                            fontFamily: '"Inter", sans-serif',
                                            color: '#475569',
                                            marginBottom: 0
                                        }}>
                                            {language === 'ar' 
                                                ? 'ملاحظاتك مهمة جداً لنا. شاركنا تجربتك مع Audiosum AI واقتراحاتك لتحسين منصتنا لخدمة احتياجاتك بشكل أفضل.'
                                                : 'Your feedback is invaluable to us. Share your experience with Audiosum AI and suggestions for improving our platform to better serve your needs.'
                                            }
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Footer />

            <style jsx>{`
                .form-control:focus {
                    border-color: #667eea !important;
                    box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25) !important;
                }
                
                .contact-form-wrapper .btn:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 5px 15px rgba(102, 126, 234, 0.4);
                }

                .contact-page {
                    direction: ${language === 'ar' ? 'rtl' : 'ltr'};
                }

                /* Keep navigation in LTR (logo on left) */
                .contact-page nav,
                .contact-page .navbar {
                    direction: ltr !important;
                }
            `}</style>
        </div>
    );
};

export default ContactUs;